import React, { useState, useEffect } from 'react';
import { Accordion } from '../common/SharedComponents';
import store, { searchActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { FeatureType, PermissionType, WatchlistLoanStatusType, WatchlistStatusType } from '@apply/constants';
import { Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus } from '@fortawesome/free-solid-svg-icons';
import { applicationInsights } from '../ApplicationInsights';
import { WatchlistApi } from '@apply/api';
import { handleAdminError } from '../common/SharedFunctions';
import WatchlistLoanDetailsModal from './modals/WatchlistLoanDetailsModal';
import { getWatchlistStatusName } from './common/WatchlistFunctions';
import { isFeatureEnabled } from '@apply/store/src/user/types';
import WatchlistSearchTable from './tables/WatchlistSearchTable';
import EditStdLoanModal from '../loans/modals/EditStdLoanModal';
import WatchlistAddLoanModal from './modals/WatchlistAddLoanModal';
import GroupItemsModal from './modals/GroupItemsModal';
import { watchlistActions } from '@apply/adminStore';
import { WatchlistSummaryTypeOptions } from '@apply/constants';
import { HelpText } from '../common/SharedComponents';
import { hasPermission } from '@apply/ui';
import { BfiCustomStore } from '@apply/ui';
import { RefreshEvents } from '@apply/ui';
import AuditHistoryModal from '../loans/modals/AuditHistoryModal';

const WatchlistBySummaryType = ({ summaryTypeId }) => {
    const searchState = useSelector((state) => state.searchState);
    const { user } = store.getState();
    if (!isFeatureEnabled(user, FeatureType.Watchlists)) {
        return <Navigate to={`/adminreferral`} />;
    }

    const { dispatch } = store;

    const watchlistsApi = new WatchlistApi.WatchlistApi(applicationInsights);
    const debouncedSearch = debounce(dispatch, 500);
    const [useWatchlistDate, set_useWatchlistDate] = useState(false);
    const [selectWatchlistId, set_selectWatchlistId] = useState('');
    const [watchlistId, set_watchlistId] = useState('');
    const [watchlists, set_watchlists] = useState([]);

    const store1 = new BfiCustomStore();
    const store2 = new BfiCustomStore();

    useEffect(() => {
        if (selectWatchlistId === 0) {
            const currentStatusWatchlist = watchlists.find((a) => a.statusId === WatchlistStatusType.Current);
            set_useWatchlistDate(false);
            set_watchlistId(currentStatusWatchlist.id);
        } else {
            set_watchlistId(selectWatchlistId);
            set_useWatchlistDate(true);
        }
    }, [selectWatchlistId]);

    const _onSearchChange = (event) => {
        debouncedSearch(
            searchActions.setSearchText({
                searchText: {
                    ...searchState.searchText,
                    watchlists: event.target.value,
                },
            })
        );
    };

    const loadWatchlistsAsync = async () => {
        try {
            const response = await watchlistsApi.getWatchlists();
            if (response.statusCode != 200) {
                throw new Error('Failed to retrive Watchlists');
            }
            set_watchlists(response.result);
        } catch (ex) {
            handleAdminError('An unexpected error occurred. Please refresh the page and try again.');
        }
    };

    useEffect(() => {
        const refreshEvents = new RefreshEvents();
        refreshEvents.addRefreshListener(loadWatchlistsAsync);

        loadWatchlistsAsync();

        return () => {
            refreshEvents.removeRefreshListener(loadWatchlistsAsync);
        };
    }, []);

    useEffect(() => {
        if (!watchlists || watchlists.length === 0) {
            return;
        }

        const search = window.location.search;
        const params = new URLSearchParams(search);
        let wlId = params.get('wlid');
        if (watchlistId) {
            wlId = watchlistId;
        }

        let wl = null;
        if (wlId) {
            wl = watchlists.find((a) => a.id == wlId);
        }
        if (!wl) {
            wl = watchlists.find((a) => a.statusId == WatchlistStatusType.Current);
        }
        if (wl) {
            set_selectWatchlistId(wl.id);
            set_watchlistId(wl.id);
        }
    }, [watchlists, summaryTypeId]);

    const currentWatchlist = () => {
        if (!watchlistId || !watchlists || watchlists.length === 0) {
            return null;
        }
        return watchlists.find((a) => a.id === watchlistId);
    };

    const getTitle = () => {
        if (!watchlistId || !watchlists || watchlists.length === 0) {
            return null;
        }

        var st = WatchlistSummaryTypeOptions.find((a) => a.id === summaryTypeId);
        var name = st?.name;

        if (!name) {
            return `Watchlist for ${currentWatchlist().description}`;
        } else {
            return `${name} Watchlist for ${currentWatchlist().description}`;
        }
    };

    const addLoan = () => {
        dispatch(
            watchlistActions.setWatchlistAddLoanModal({
                watchlistAddLoanModal: {
                    isOpen: true,
                    watchlistId,
                    summaryTypeId,
                },
            })
        );
    };

    const groupItems = () => {
        dispatch(
            watchlistActions.setGroupItemsModal({
                groupItemsModal: { isOpen: true, watchlistId, summaryTypeId },
            })
        );
    };

    const getCurrentWatchlistStyle = () => {
        const wl = watchlists.find((a) => a.id === watchlistId);
        return getWatchlistStyle(wl);
    };

    const getWatchlistStyle = (wl) => {
        switch (wl.statusId) {
            case WatchlistStatusType.Current:
                return { backgroundColor: '#a9dba9', fontWeight: 'bold' };
            case WatchlistStatusType.Draft:
                return { backgroundColor: '#d1defa' };
            case undefined:
                return { backgroundColor: '#ebf9e9', fontStyle: 'italic' };
            default:
                return { backgroundColor: '#ebe9e9' };
        }
    };

    if (!summaryTypeId || !watchlistId || currentWatchlist() == null) {
        return;
    }

    return (
        <>
            <div
                className='page-header'
                role='banner'
            >
                <h1>{getTitle()}</h1>
                <div className='primary-actions'>
                    <button
                        className='hollow'
                        onClick={() => addLoan()}
                        title='Add Loan / Property to the selected watchlist'
                        disabled={
                            !hasPermission(PermissionType.WatchlistEdit) ||
                            currentWatchlist().statusId === WatchlistStatusType.Historic
                        }
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        &nbsp;Add Loan / Property
                    </button>
                    &nbsp;
                    <button
                        className='hollow'
                        onClick={() => groupItems()}
                        title='Group Loans / Properties'
                        disabled={
                            !hasPermission(PermissionType.WatchlistEdit) ||
                            currentWatchlist().statusId === WatchlistStatusType.Historic
                        }
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        &nbsp;Add Group
                    </button>
                    &nbsp;
                    <HelpText />
                </div>
            </div>
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div>
                    <div
                        style={{
                            width: 'calc(100% - 360px)',
                            borderBottom: '1px solid #cfcfe7',
                            padding: '.5625rem 0 0 0.937rem',
                            height: '58px',
                            position: 'relative',
                            float: 'left',
                        }}
                    >
                        <input
                            type='search'
                            placeholder='Search on Borrower Name or BFI Number'
                            defaultValue={searchState?.searchText?.watchlists}
                            onChange={_onSearchChange}
                            style={{ paddingLeft: '30px' }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{
                                left: '1.4375rem',
                                position: 'absolute',
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }}
                        />
                    </div>
                    <div
                        className='main-search2'
                        style={{ width: '350px' }}
                    >
                        <select
                            id='watchlistId'
                            value={selectWatchlistId}
                            onChange={(event) => set_selectWatchlistId(parseInt(event.target.value))}
                            style={getCurrentWatchlistStyle()}
                        >
                            {watchlists.map((fs) => {
                                return (
                                    <>
                                        <option
                                            key={fs.id}
                                            value={fs.id}
                                            style={getWatchlistStyle(fs)}
                                        >
                                            {fs.description} - {getWatchlistStatusName(fs.statusId)}
                                        </option>
                                        {fs.statusId === WatchlistStatusType.Current && (
                                            <option
                                                key='today'
                                                value={0}
                                                style={getWatchlistStyle({ statusId: undefined })}
                                            >
                                                Today
                                            </option>
                                        )}
                                    </>
                                );
                            })}
                        </select>
                    </div>
                </div>
                <div
                    className='scrollable-content'
                    style={{ padding: '1rem' }}
                >
                    <div
                        className='watchlists accordion'
                        data-accordion=''
                        data-allow-all-closed='true'
                        data-multi-expand='true'
                    >
                        {/* <Accordion
                            key='activeWatchlist'
                            title='Uncured'
                        > */}
                        <div className='card'>
                            <div className='card-section'>
                                <WatchlistSearchTable
                                    watchlistId={watchlistId}
                                    useWatchlistDate={useWatchlistDate}
                                    watchlistName={currentWatchlist().description}
                                    summaryTypeId={summaryTypeId}
                                    watchlistLoanStatusId={WatchlistLoanStatusType.Uncured}
                                    gridStore={store1}
                                />
                            </div>
                        </div>
                        {/* </Accordion> */}
                        <Accordion
                            key='curedWatchlist'
                            title='Cured but Outstanding'
                        >
                            <div className='card'>
                                <div className='card-section'>
                                    <WatchlistSearchTable
                                        watchlistId={watchlistId}
                                        useWatchlistDate={useWatchlistDate}
                                        watchlistName={currentWatchlist().description}
                                        summaryTypeId={summaryTypeId}
                                        watchlistLoanStatusId={WatchlistLoanStatusType.CuredButOutstanding}
                                        gridStore={store2}
                                    />
                                </div>
                            </div>
                        </Accordion>
                    </div>
                </div>
            </div>
            <WatchlistLoanDetailsModal />
            <WatchlistAddLoanModal />
            <EditStdLoanModal />
            <GroupItemsModal />
            <AuditHistoryModal />
        </>
    );
};

export default WatchlistBySummaryType;
